// Shared upload-job lifecycle helper — keeps the library views (dashboard recent
// uploads + AlbumView cards) in sync with the backend `upload_jobs` status so we
// can show a processing progress meter and failed/rejected state, matching the
// mobile apps. Backend statuses come from /user/upload-history.

// Statuses that mean "still being certified / registered on chain".
const PROCESSING_STATUSES = new Set([
  "UPLOADED",
  "HASHED",
  "PENDING",
  "PENDING_CHAIN",
  "PROCESSING",
  "QUEUED",
  "UPLOADING",
  "REGISTERING",
]);

export type UploadPhase = "processing" | "verifying" | "review" | "failed" | "done";

export interface UploadPhaseInfo {
  phase: UploadPhase;
  progress: number;        // 0–100
  reason?: string;         // rejection / failure reason, when failed
  label: string;           // short human label for a badge
}

/** Reads the lifecycle of a file/upload item (works on FileItem or its `.meta`). */
export function getUploadPhase(file: any): UploadPhaseInfo {
  const meta = file?.meta ?? {};
  const status = String(file?.status ?? meta.status ?? "").toUpperCase();
  const isRejected =
    file?.isRejected === true ||
    meta.isRejected === true ||
    status === "REJECTED";
  const blockchainRegistered =
    file?.blockchainRegistered === true || meta.blockchainRegistered === true;
  const progressRaw = file?.progress ?? meta.progress;
  const progress = Math.max(0, Math.min(100, Number(progressRaw ?? 0)));

  // An uploaded original is being matched against this (rejected) media. Show a
  // distinct "Verifying" state rather than the plain rejected badge, and keep
  // polling until the match resolves.
  const verifyingOriginal =
    file?.verifyingOriginal === true || meta.verifyingOriginal === true;
  if (verifyingOriginal) {
    return { phase: "verifying", progress: progress > 0 ? progress : 8, label: "Verifying" };
  }

  // Deepfake-flagged media held for a human decision — a neutral state, checked
  // BEFORE failed because the backend represents it as FAILED + isUnderReview.
  const isUnderReview = file?.isUnderReview === true || meta.isUnderReview === true;
  if (isUnderReview) {
    return { phase: "review", progress: 100, label: "Under review" };
  }

  if (isRejected || status === "FAILED" || status === "ERROR") {
    return {
      phase: "failed",
      progress,
      reason:
        file?.rejectionReason ??
        meta.rejectionReason ??
        meta.statusMessage ??
        meta.errorMessage ??
        "This media could not be certified.",
      label: isRejected ? "Rejected" : "Failed",
    };
  }

  const done =
    blockchainRegistered ||
    status === "COMPLETED" ||
    status === "CONFIRMED" ||
    status === "ACTIVE" ||
    status === "";

  if (!done && PROCESSING_STATUSES.has(status)) {
    return {
      phase: "processing",
      // Show at least a little progress so the bar never looks stuck at 0.
      progress: progress > 0 ? progress : 8,
      label: "Processing",
    };
  }

  return { phase: "done", progress: 100, label: "Protected" };
}

/** True when any item in the list is still processing (used to drive polling). */
export function hasProcessingUploads(files: any[]): boolean {
  return files.some((f) => {
    const phase = getUploadPhase(f).phase;
    return phase === "processing" || phase === "verifying";
  });
}
